import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';
import ServiceButtons from '../../components/feature/ServiceButtons';

export default function Home() {
  useEffect(() => {
    // Add Schema.org JSON-LD for homepage
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "name": "Virtus Realty Appraisal Services LLC",
      "description": "Professional property appraisal services in New Jersey. Certified General appraisers for residential, commercial, and industrial properties.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
      "telephone": "(551) 206-2485",
      "email": "virtusappraisalsnj@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "NJ",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "40.0583",
        "longitude": "-74.4057"
      },
      "areaServed": {
        "@type": "State",
        "name": "New Jersey"
      },
      "priceRange": "$$",
      "openingHours": "Mo-Fr 09:00-17:00",
      "sameAs": [
        "https://www.facebook.com/virtusappraisals",
        "https://www.linkedin.com/company/virtusappraisals"
      ]
    });
    document.head.appendChild(script);
    
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-building-line',
      title: 'Commercial & Industrial',
      description: 'Professional appraisals for office buildings, retail spaces, warehouses, and industrial properties.',
      features: ['Income Approach Analysis', 'Market Comparables', 'Cost Approach Valuation']
    },
    {
      icon: 'ri-home-line',
      title: 'Residential & Multi-Family',
      description: 'Accurate valuations for single-family homes, condos, townhomes, and multi-family properties.',
      features: ['Single Family Homes', 'Condominiums', 'Multi-Family Properties']
    },
    {
      icon: 'ri-scales-line',
      title: 'Estate, Trust & Divorce Valuations',
      description: 'Specialized appraisals for estate planning, trust administration, inheritance, and divorce cases.',
      features: ['Estate Planning', 'Trust Administration', 'Inheritance Valuations', 'Divorce Appraisals']
    },
    {
      icon: 'ri-scales-3-line',
      title: 'Tax Appeal & Litigation',
      description: 'Expert testimony and detailed reports for tax appeals, legal proceedings, and dispute resolution.',
      features: ['Expert Testimony', 'Tax Appeal Support', 'Legal Documentation']
    },
    {
      icon: 'ri-bank-line',
      title: 'Bank & Mortgage Financing',
      description: 'Reliable appraisals for lending institutions, refinancing, and mortgage origination.',
      features: ['Lending Support', 'Refinancing', 'Mortgage Origination']
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Market Analysis',
      description: 'Comprehensive market rent studies, feasibility analysis, and investment property evaluations.',
      features: ['Rent Studies', 'Feasibility Analysis', 'Investment Analysis']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/b2c28b58017dfd1154376718b00eda5d.jpg')`
        }}
      >
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight font-elegant">
              Trusted <span className="text-gradient-gold">NJ Certified</span> Appraisal Services
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed font-modern">
              Precise, compliant, and timely valuation reports for commercial, residential, and industrial properties throughout your New Jersey.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button size="lg" className="gradient-gold text-black-900 hover:shadow-gold font-semibold">
                  <i className="ri-phone-line mr-2"></i>
                  Get Free Quote
                </Button>
              </Link>
              <Link to="/about">
                <Button size="lg" variant="outline" className="border-gold-400 text-gold-400 hover:bg-gold-400 hover:text-black-900 font-semibold">
                  <i className="ri-play-circle-line mr-2"></i>
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Secondary Hero Section */}
      <section 
        className="relative py-24 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.9)), url('https://readdy.ai/api/search-image?query=Elegant%20architectural%20blueprint%20drawings%20and%20measuring%20tools%20on%20white%20marble%20surface%2C%20minimalist%20professional%20workspace%2C%20soft%20natural%20lighting%2C%20subtle%20shadows%2C%20clean%20modern%20aesthetic%20with%20gold%20accents&width=1920&height=800&seq=secondary-hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-black-900 mb-6 leading-tight font-elegant">
              Certified General Appraiser providing services built on 
              <span className="text-gradient-gold"> accuracy, integrity, and trust</span>.
            </h2>
            <p className="text-lg md:text-xl text-gray-700 mb-8 font-modern tracking-wide">
              Residential • Commercial • Industrial • Special Purpose Properties<br />
              Estate, Divorce & Tax Appeal Appraisals, and more.
            </p>
            <Link to="/contact">
              <Button size="lg" className="gradient-gold text-black-900 hover:shadow-gold font-semibold">
                <i className="ri-arrow-right-line mr-2"></i>
                Request an Appraisal
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-black-900 mb-4 font-elegant">
              Comprehensive Appraisal Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-modern">
              Our team of Certified General Appraisers delivers precise valuations for banks, lenders, attorneys, accountants, and private clients.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-gold transition-all duration-300 hover:border-gold-300">
                <div className="w-12 h-12 gradient-gold rounded-lg flex items-center justify-center mb-4">
                  <i className={`${service.icon} text-black-900 text-xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-black-900 mb-3 font-elegant">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4 font-modern">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600 font-modern">
                      <i className="ri-check-line text-gold-500 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" className="gradient-gold text-black-900 hover:shadow-gold font-semibold">
                View All Services
                <i className="ri-arrow-right-line ml-2"></i>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-black-900 mb-6 font-elegant">
                Why Choose Virtus Realty Appraisal Services?
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 gradient-gold rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-shield-check-line text-black-900 text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-black-900 mb-2 font-elegant">Certified General Appraisers</h3>
                    <p className="text-gray-600 font-modern">Our team holds the highest level of appraisal certification, ensuring expertise across all property types and values.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 gradient-gold rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-time-line text-black-900 text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-black-900 mb-2 font-elegant">Fast Turnaround</h3>
                    <p className="text-gray-600 font-modern">We understand time-sensitive transactions and deliver comprehensive reports within a specified and honorable timeframe.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 gradient-gold rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-map-pin-line text-black-900 text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-black-900 mb-2 font-elegant">Local Market Expertise</h3>
                    <p className="text-gray-600 font-modern">Deep knowledge of local market conditions, zoning regulations, and economic factors affecting property values.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 gradient-gold rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-file-text-line text-black-900 text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-black-900 mb-2 font-elegant">Detailed Reporting</h3>
                    <p className="text-gray-600 font-modern">Comprehensive reports that meet all regulatory standards and provide clear, defensible valuation conclusions.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://static.readdy.ai/image/0c5a07476bd7a7109b05d9670ab2ad74/5f1038eef24f673c58de364c993e7579.png"
                alt="Professional appraiser conducting property inspection"
                className="rounded-xl shadow-lg object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-elegant">
            Ready to Get Your Property Appraised?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto font-modern">
            Contact us today for a free consultation and quote. Our certified appraisers are ready to provide you with accurate, timely valuations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/contact">
              <Button size="lg" className="gradient-gold text-black-900 hover:shadow-gold font-semibold">
                <i className="ri-phone-line mr-2"></i>
                Get Free Quote
              </Button>
            </Link>
            <Link to="/about">
              <Button size="lg" variant="outline" className="border-gold-400 text-gold-400 hover:bg-gold-400 hover:text-black-900 font-semibold">
                <i className="ri-play-circle-line mr-2"></i>
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <ServiceButtons />
    </div>
  );
}
